<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" persistent fullscreen scrollable)
  v-card.neutral200
    //- Top bar
    v-card-title.px-4
      v-btn.mr-2(icon color="neutral700" @click="closeDialog")
        v-icon mdi-close
      .text-2xl.font-semibold {{ $t('visit_summary') }}
      v-spacer
      
    v-card-text.neutral700--text(outlined)
      .d-flex.justify-center.my-6
        div(style="width: 70%;")
          //- No data
          div.py-6.px-10.round-lg.white(v-if="isFetching")
            v-skeleton-loader(type="list-item-three-line@5")
          template(v-else)
            //- Patient detail
            .text-xl.font-semibold {{ patient.name }}
            template(v-for="item in items")
              v-row.mt-2(no-gutters)
                v-col(cols="2")
                  .text-sm.font-semibold {{ item.text }}
                v-col.text-left
                  .d-flex
                    .text-sm.font-semibold.mr-3 :
                    .text-sm {{ item.format && patient[item.value] ? item.format(patient[item.value]) : patient[item.value] }}
            
            .text-md.font-semibold.mt-6 {{ $t('visit_summary') }}
            //- Filters
            div.my-4
              .d-flex.align-center
                div Show:
                template(v-for="(enabled, section) in sections")
                  div.ml-4
                    y-checkbox.mb-n2(v-model="sections[section]" @change="updateShowSections")
                      template(#label)
                        .text-sm.neutral700--text.capitalize {{ section }}
                v-spacer
                div.d-flex.align-center.ml-2
                  v-btn(icon color="neutral500" @click="onClickSearch")
                    v-icon(size="20") $yzi-search-sm
                  v-text-field(v-if="showSearchField" v-model="searchText" @blur="onBlurSearch" @input="queryChanged" solo dense flat placeholder="Type to search..." hide-details clearable autofocus)
                div.ml-2
                  v-menu(offset-y bottom left)
                    template(v-slot:activator="{ on }")
                      v-btn(v-if="selectedSort.text === $t('clear_sort')" icon color="neutral500" v-on="on")
                        v-icon(size="20") $yzi-filter-line
                      v-btn.selected-button(v-else text small color="neutral" v-on="on")
                        v-icon(size="20") $yzi-menu-03
                        v-icon(size="13") {{ selectedSort.text === $t('newest_to_oldest') ? '$yzi-arrow-up' : '$yzi-arrow-down' }}
                    v-list.py-0(min-width="200px")
                      .text-md.font-semibold.px-4.py-2 {{ $t('sort') }}
                      template(v-for="(sort, sortItemIndex) in sortItems")
                        v-divider(v-if="sortItemIndex !== 0")
                        v-list-item(@click="onSelectSort(sort)")
                          v-list-item-content
                            v-list-item-title.text-sm(:class="sort.text === $t('clear_sort') ? 'danger500--text' : ''") {{ sort.text }}
                div.ml-2
                  v-menu(offset-y bottom left)
                    template(v-slot:activator="{ on }")
                      v-btn.text-none(small rounded outlined v-on="on")
                        span.text-sm.mr-1 {{ selectedRange.text }}
                        v-icon(size="16") $yzi-chevron-down
                    v-list.py-0(min-width="200px")
                      template(v-for="(range, historyRangeIndex) in historyRange")
                        v-divider(v-if="historyRangeIndex !== 0")
                        v-list-item(@click="onSelectHistoryRange(range)")
                          v-list-item-content
                            v-list-item-title.text-sm {{ range.text }}

            template(v-for="(visit, index) in patientHistories")
              div.pt-6.pb-1.px-10.mb-6.round-xl.white.content-box
                //- Date
                .text-lg.font-semibold.mb-10 {{ visit.formatted_date }}
                .text-lg.font-semibold.mb-4(v-if="!visit.consultation")  OTC
                //- Provider (remove if coming from OTC)
                div.mb-10(v-if="visit.consultation")
                  visit-summary-provider(:providerName="visit.doctor_name")
                //- Diagnosis (remove if coming from OTC)
                div.mb-10(v-if="visit.consultation && sections.diagnosis")
                  visit-summary-diagnosis(:leadPid="visit.lead_pid" :diagnosis="getDiagnosis(visit)" @updated="onUpdateDiagnosis($event, visit)")
                //- Notes (remove if coming from OTC)
                div.mb-10(v-if="visit.consultation && sections.notes")
                  visit-summary-notes(:consultation="getConsultation(visit)" :provider="visit.provider" :leadPid="visit.lead_pid" :patientId="patient.id" :page="currentPage" source="patient-history")
                //- Medication
                div.mb-10(v-if="sections.medication")
                  visit-summary-medication(:patientId="patient.id" :leadPid="visit.lead_pid" :medications="visit.medications" :page="currentPage" source="patient-history")
                //- Services
                div.mb-10(v-if="sections.services")
                  visit-summary-services(:patientId="patient.id" :leadPid="visit.lead_pid" :services="visit.services" :page="currentPage" source="patient-history")
                //- Files (remove if coming from OTC)
                div.mb-10(v-if="visit.consultation && sections.files")
                  visit-summary-file(:leadPid="visit.lead_pid" :patientId="patient.id" :items="visit.documents" @updated="$emit('updated')")
                //- Documents (remove if coming from OTC)
                div.mb-10(v-if="visit.consultation && sections.documents")
                  visit-summary-document(:leadPid="visit.lead_pid" :patientId="patient.id" :items="visit.documents" :page="currentPage" source="patient-history")

            template(v-if="!patientHistories.length")
              .mt-10.text-md.neutral700--text.text-center {{ $t('no_list_available') }}

    v-divider
    v-card-actions.px-6.white
      v-row(no-gutters)
        v-col(offset="2" cols="8")
          div.d-flex.justify-center
            yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" attach menu-top @input="pageChanged")
        v-col(cols="2" align="right")
          v-btn.button-md(color="neutral700" depressed dark @click="closeDialog") {{ $t('done') }}
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { DateTime } from 'luxon'
import { debounce } from '@/utils'
import VisitSummaryProvider from '@/components/clinic/visitsummary/VisitSummaryProvider.vue'
import VisitSummaryDiagnosis from '@/components/clinic/visitsummary/VisitSummaryDiagnosis.vue'
import VisitSummaryNotes from '@/components/clinic/visitsummary/VisitSummaryNotes.vue'
import VisitSummaryMedication from '@/components/clinic/visitsummary/VisitSummaryMedication.vue'
import VisitSummaryServices from '@/components/clinic/visitsummary/VisitSummaryServices.vue'
import VisitSummaryFile from '@/components/clinic/visitsummary/VisitSummaryFile.vue'
import VisitSummaryDocument from '@/components/clinic/visitsummary/VisitSummaryDocument.vue'

export default {
  components: {
    VisitSummaryProvider, VisitSummaryDiagnosis, VisitSummaryNotes, VisitSummaryMedication, VisitSummaryServices, VisitSummaryFile, VisitSummaryDocument
  },
  props: {
    open: {
      type: Boolean,
    },
    patient: {
      required: true,
      type: Object,
      default: null
    },
  },
  data () {
    return {
      isFetching: false,
      showSearchField: false,
      searchText: null,
      totalCount: 0,
      pageSize: 20,
      currentPage: 1,
      patientHistories: [],
      selectedRange: { text: this.$t('all_time'), value: null },
      selectedSort: { text:this.$t('clear_sort'), value:null },
      historyRangeOptions: [
        { text: this.$t('all_time'), value: null },
        { text: this.$t('past_1_month'), value: 1 },
        { text: this.$t('past_3_months'), value: 3 },
        { text: this.$t('past_6_months'), value: 6 },
        { text: this.$t('past_12_months'), value: 12 },
      ],
      sortOptions: [
        { text:this.$t('newest_to_oldest'), value:'-created' },
        { text:this.$t('oldest_to_newest'), value:'created' },
        { text:this.$t('clear_sort'), value:null },
      ],
      items: [
        { text: 'Gender', value: 'gender', format: (item) => { return this.$options.filters.capitalize(item) } },
        { text: 'Age', value: 'dob', format: (item) => { return `${this.$options.filters.displayAgeByYearMonthDay(this.$options.filters.getAgeByYearMonthDay(item))} old` } },
        { text: 'D.O.B', value: 'dob', format: (item) => { return this.displayDate(item) } },
        { text: 'ID', value: 'ic', format: null }
      ]
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      visitSummarySections: 'clinic/getVisitSummarySections',
    }),
    showDialog: {
      get () {
        return this.open
      },
      set (newValue) {
        this.$emit('update:open', newValue)
      }
    },
    historyRange() {
      return this.historyRangeOptions.filter(item => this.selectedRange.text !== item.text)
    },
    sortItems() {
      return this.sortOptions.filter(item => this.selectedSort.text !== item.text)
    },
    sections () {
      return this.visitSummarySections
    }
  },
  watch: {
    currentPage () {
      this.fetchPatientHistory()
    },
    selectedRange () {
      this.currentPage = 1
      this.fetchPatientHistory()
    },
    selectedSort () {
      this.currentPage = 1
      this.fetchPatientHistory()
    },
  },
  mounted () {
    if (this.$route.params?.savePage) {
      this.currentPage = Number(this.$route.params?.savePage)
      // clear params
      // this.$router.replace({})
    }
    this.fetchPatientHistory()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchPatientHistory() {
      this.isFetching = true
      const params = {
        search: this.searchText,
        past_months: this.selectedRange.value,
        page_size: this.pageSize,
        page: this.currentPage,
        ordering: this.selectedSort.value
      }
      this.$axios.get(`/clinics/${this.company.slug}/patients/${this.patient.id}/histories`, { params })
        .then(resp => {
          let histories = []
          this.totalCount = resp.data.count
          resp.data.results.forEach(item => {
            histories.push(this.sanitizeData(item))
          })
          this.patientHistories = histories
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data?.detail) {
            message = err.response.data.detail
          }
          this.showMessage(['error', message])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    sanitizeData (data) {
      if (data.treatments.length) {
        data.medications = data.treatments.filter(f => ['medication', 'equipment', 'consumable'].includes(f.product.type))
        data.services = data.treatments.filter(f => ['service', 'procedure', 'investigation', 'consultation'].includes(f.product.type))
        data.documents = data.treatments.filter(f => f.product.type === 'documents')
      }
      // Record date
      const formatted_date = data.record_date ? this.displayVisitDate(data.record_date) : null
      // Doctor
      const doctor_name = data.provider ? data.provider.name : null
      // Diagnoses
      const formatted_diagnoses = data?.consultation?.diagnoses ? this.filterDiagnoses(data.consultation.diagnoses) : null
      // Payment methods
      const all_payment_methods = data?.transactions ? this.formatPaymentMethods(data.transactions) : []
      const payment_methods = data?.transactions ? this.filterPaymentMethods(all_payment_methods) : null
      return {
        ...data,
        formatted_date,
        doctor_name,
        formatted_diagnoses,
        payment_methods,
        all_payment_methods
      }
    },
    pageChanged (page) {
      this.currentPage = page
    },
    onSelectHistoryRange (selectedRange) {
      this.selectedRange = selectedRange
    },
    onSelectSort (selectedSort) {
      this.selectedSort = selectedSort
    },
    queryChanged: debounce(function () {
      this.currentPage = 1
      this.fetchPatientHistory()
    }, 750),
    onClickSearch() {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false
        }
      } else {
        this.showSearchField = true
      }
    },
    onBlurSearch() {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false
      }
    },
    getConsultation (visit) {
      const consultation = visit.consultation || null
      if (consultation) return consultation
      return {}
    },
    getDiagnosis (visit) {
      const diagnosis = visit?.consultation?.diagnoses || null
      if (diagnosis) return diagnosis
      return []
    },
    updateShowSections () {
      const key = 'yz_visit_summary_sections'
      localStorage.setItem(key, JSON.stringify(this.sections))
    },
    closeDialog () {
      this.$emit('closed')
      this.showDialog = false
    },
    onUpdateDiagnosis (diagnosis, visit) {
      this.$set(visit.consultation, 'diagnoses', diagnosis)
      this.$emit('updated')
    },
    displayDate (value) {
      if (!value) {
        return null
      }
      const date = DateTime.fromISO(value)
      return date.toFormat('dd MMM yyyy')
    },
    displayVisitDate (value) {
      if (!value) return null

      const date = DateTime.fromISO(value)
      const formatDate = date.toFormat('d MMM yyyy')
      const formatTime = date.toFormat('H:mm a').toLowerCase()

      return `${formatDate}, ${formatTime}`
    },
    filterDiagnoses (array) {
      if (array.length > 1) {
        let newArray = []
        let count = 0
        array.forEach((o, idx) => {
          count += o.length
          if (idx === 0 || count <= 25) {
            newArray.push(o)
          }
        })
        return newArray
      }
      return array
    },
    formatPaymentMethods (array) {
      return array.map((item) => {
        let resp = item.payment_method
        if (item.channel === 'panel') {
          resp = 'Panel: ' + resp
        }
        return resp
      })
    },
    filterPaymentMethods (array) {
      if (array.length > 1) {
        let newArray = []
        let count = 0
        array.forEach((o, idx) => {
          count += o.length
          if (idx === 0 || count <= 25) {
            newArray.push(o)
          }
        })
        return newArray
      }
      return array
    }
  },
}
</script>

<style lang="scss" scoped>
.content-box {
  border: 1px var(--v-neutral300) solid !important;
}
</style>